import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Book, GraduationCap, Home, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';

const Navigation = () => {
  const location = useLocation();
  const { isAdmin, devModeEnabled, toggleDevMode } = useAuth();
  
  // Check if we're running on localhost
  const isLocalhost = window.location.hostname === 'localhost';
  
  // Base navigation items
  const baseNavItems = [
    { icon: <Home className="w-6 h-6" />, label: 'Home', path: '/home' },
    { icon: <Book className="w-6 h-6" />, label: 'Learn', path: '/learn' },
    { icon: <User className="w-6 h-6" />, label: 'Profile', path: '/profile' },
  ];
  
  // Admin-only navigation items
  const adminNavItems = [
    { icon: <GraduationCap className="w-6 h-6" />, label: 'Lessons', path: '/lessons' },
  ];
  
  // Combine based on user role
  const navItems = isAdmin 
    ? [...baseNavItems.slice(0, 2), ...adminNavItems, ...baseNavItems.slice(2)] 
    : baseNavItems;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-t border-border md:relative md:bottom-auto md:left-auto md:right-auto md:border-none md:backdrop-blur-none md:bg-transparent md:border-t-0">
      <nav className="container max-w-md mx-auto">
        <ul className="flex justify-between items-center py-2 md:py-4">
          {navItems.map((item) => (
            <li key={item.path}>
              <Link 
                to={item.path} 
                className={cn(
                  "flex flex-col items-center px-2",
                  item.path === '/profile' && isLocalhost && "relative"
                )}
              >
                <div 
                  className={cn(
                    "p-2 rounded-full transition-all duration-300 ease-in-out relative", 
                    location.pathname === item.path 
                      ? "text-primary bg-primary/10" 
                      : "text-muted-foreground hover:text-primary hover:bg-primary/5"
                  )}
                >
                  {item.icon}
                  
                  {/* Dev mode indicator on profile icon */}
                  {isLocalhost && item.path === '/profile' && (
                    <div 
                      onClick={(e) => {
                        e.preventDefault(); // Prevent navigation
                        e.stopPropagation(); // Stop event bubbling
                        toggleDevMode();
                      }}
                      className={cn(
                        "absolute -top-1 -right-1 w-3 h-3 rounded-full border border-white shadow-sm cursor-pointer",
                        devModeEnabled 
                          ? "bg-yellow-400" // Yellow for dev mode ON
                          : "bg-blue-500"   // Blue for real auth
                      )}
                      title={devModeEnabled ? "Dev Mode: ON (click to toggle)" : "Real Auth (click to toggle)"}
                    />
                  )}
                </div>
                <span 
                  className={cn(
                    "text-xs mt-1 transition-colors duration-300", 
                    location.pathname === item.path 
                      ? "text-primary font-medium" 
                      : "text-muted-foreground",
                    isLocalhost && item.path === '/profile' && devModeEnabled && "text-yellow-600" // Highlight profile text in dev mode
                  )}
                >
                  {item.label}
                  {isLocalhost && item.path === '/profile' && devModeEnabled && " (Dev)"}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
};

export default Navigation;
